/**
 * HubTransition — direction-aware content-area slide wrapper.
 *
 * Wraps any mode's content in a div that receives CSS @keyframe
 * animations keyed by the navigation direction (forward/backward).
 * The animation re-triggers whenever `modeKey` changes — the parent
 * passes a stable key string that uniquely identifies the current
 * content pane:
 *
 *   - Most modes: use the mode name itself.
 *   - "detail" mode: use `detail-${selectedDocId}` so switching
 *     between different docs also triggers the transition (same
 *     mode, different content).
 *
 * Forward  → new content slides in from the right (drill in).
 * Backward → new content slides in from the left (back stack pop).
 *
 * Animation values mirror the panel's own timing:
 *   duration  220ms (PANEL_SLIDE_MS)
 *   easing    cubic-bezier(0.2, 0.8, 0.2, 1)
 *
 * The wrapper itself is `flex: 1` + `overflow: hidden` so it fills
 * the panel's remaining height without introducing a second scroll
 * container. The child is responsible for its own internal scroll.
 *
 * NOTE: makeStyles does not support dynamic @keyframes in v9.  We
 * use inline `style` for the animation-name so the direction can
 * change at runtime without class regeneration.
 */

import { makeStyles } from "@fluentui/react-components";
import type React from "react";
import type { NavDirection } from "../hooks/useHubNavigation";

export interface HubTransitionProps {
  /** Stable key for the current content pane. Changing this value
   *  re-mounts the wrapper div and re-runs the entrance animation. */
  modeKey: string;
  direction: NavDirection;
  children: React.ReactNode;
}

// CSS @keyframes injected once at module evaluation time.  We use
// a <style> tag rather than makeStyles because v9 shaker-based
// makeStyles can't produce @keyframes blocks with dynamic names.
const STYLE_ID = "hub-transition-keyframes";
if (typeof document !== "undefined" && !document.getElementById(STYLE_ID)) {
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = `
    @keyframes hub-slide-in-forward {
      from { opacity: 0; transform: translateX(18px); }
      to   { opacity: 1; transform: translateX(0); }
    }
    @keyframes hub-slide-in-backward {
      from { opacity: 0; transform: translateX(-18px); }
      to   { opacity: 1; transform: translateX(0); }
    }
  `;
  document.head.appendChild(el);
}

const ANIM_DURATION = "220ms";
const ANIM_EASING = "cubic-bezier(0.2, 0.8, 0.2, 1)";

const useStyles = makeStyles({
  wrapper: {
    flex: 1,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },
  inner: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    animationDuration: ANIM_DURATION,
    animationTimingFunction: ANIM_EASING,
    animationFillMode: "both",
  },
});

export const HubTransition: React.FC<HubTransitionProps> = ({ modeKey, direction, children }) => {
  const styles = useStyles();
  const animationName = direction === "forward" ? "hub-slide-in-forward" : "hub-slide-in-backward";

  return (
    <div className={styles.wrapper} data-testid="hub-transition-wrapper">
      {/* key forces React to remount the inner div on every modeKey
          change, re-triggering the CSS animation from frame 0. */}
      <div key={modeKey} className={styles.inner} style={{ animationName }} data-testid="hub-transition-inner">
        {children}
      </div>
    </div>
  );
};

export default HubTransition;
